import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchBookingConsole,
    addShopType,
    updateShopType,
    deleteShopType,
    addResource,
    updateResource,
    deleteResource,
    createBooking,
    updateBooking,
    updateBookingStatus,
    rescheduleBooking,
    clearBookingError,
    clearBookingNotice,
    addShop
} from "../../Store/Slices/Tenant/Booking";
import { isActiveBooking } from "./bookingService";
import { getShopCtx, setShopCtx, ShopSwitcher } from "./components/shop/ShopBits";
import BookingShell from "./components/BookingShell";
import ShopOverview from "./components/shop/ShopOverview";
import CalendarTab from "./components/shop/CalendarTab";
import ShopBookings from "./components/shop/ShopBookings";
import CustomersTab from "./components/shop/CustomersTab";
import CalendarsTab from "./components/shop/CalendarsTab";
import PortalTab from "./components/shop/PortalTab";
import WidgetTab from "./components/shop/WidgetTab";

const META = {
    overview: { title: "Overview", subtitle: "Today, pending requests and plan usage" },
    calendar: { title: "Calendar", subtitle: "Day, week and month across your resources" },
    bookings: { title: "Bookings", subtitle: "Confirm, edit, move and cancel customer bookings" },
    customers: { title: "Customers", subtitle: "Directory built from your bookings" },
    calendars: { title: "Resources", subtitle: "Doctors, rooms, calendars and slot settings" },
    portal: { title: "Client portal", subtitle: "Exactly what your customers see" },
    widget: { title: "Website widget", subtitle: "Attach booking to your own website" },
};

/**
 * Shop booking console in the Nekobiz workspace layout.
 * Each shop manages its own calendars, resources, customers and bookings.
 *
 * Props (both optional, used by the admin console to impersonate a shop):
 *   impersonate = shop object to lock the console to (hides the switcher)
 *   onExit      = called when the admin exits impersonation
 *
 * Exported both as named (`Booking`) and default export.
 */
export function Booking({ impersonate, onExit } = {}) {
    const dispatch = useDispatch();
    const { plans, shops, resourceTypes, resources, bookings, loading, error, notice } = useSelector(
        (state) => state.booking
    );

    const [shopId, setShopId] = useState((impersonate && impersonate.id) || "");
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        dispatch(fetchBookingConsole());
    }, [dispatch]);

    useEffect(() => {
        if (impersonate && impersonate.id) setShopId(impersonate.id);
    }, [impersonate]);

    useEffect(() => {
        if (!impersonate && !shopId && shops.length) setShopId(getShopCtx(shops));
    }, [shops, shopId, impersonate]);

    const shop = shops.find((s) => s.id === shopId) || null;

    const pickShop = (id) => {
        setShopId(id);
        setShopCtx(id);
    };

    const myBookings = useMemo(
        () => bookings.filter((b) => b.ownerId === shopId),
        [bookings, shopId]
    );
    const pendingCount = useMemo(
        () => myBookings.filter((b) => b.status === "pending").length,
        [myBookings]
    );
    const customerCount = useMemo(() => {
        const keys = new Set(
            myBookings.map((b) => `${(b.customer || "").trim().toLowerCase()}|${(b.email || "").trim().toLowerCase()}`)
        );
        return keys.size;
    }, [myBookings]);

    const shopNameRef = React.useRef(null);
    const submittingRef = React.useRef(false);

    const handleCreateShop = async (e) => {


        const shopname = shopNameRef.current?.value?.trim();

        if (!shopname || shopname.length < 4 || shopname.length > 64) {
            alert("Shop name must be between 4 and 64 characters.");
            return;
        }

        // Immediate lock — prevents a second click before React re-renders
        submittingRef.current.disabled = true;

        try {
            await dispatch(addShop({ shopname })).unwrap();
        } catch (error) {
            console.error("Failed to create shop:", error);

            // Allow retry after failure
            submittingRef.current.disabled = false;
        }
        window.location.reload();
    };

    if (loading && shops.length === 0) {
        return (
            <div className="hk-page">
                <div className="hk-empty">
                    <div className="hk-empty-icon">🏪</div>
                    <div className="font-bold text-zinc-700">Loading shop console…</div>
                </div>
            </div>
        );
    }

    if (!shop) {
return (
    <div className="hk-page min-h-[70vh] flex items-center justify-center px-4 py-10">
        <div className="hk-card w-full max-w-md p-6 sm:p-8">

            {/* Header */}
            <div className="text-center">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.7"
                        stroke="currentColor"
                        className="h-7 w-7"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01"
                        />
                    </svg>
                </div>

                <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                    Create your shop
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                    Set up your shop to start managing your business.
                </p>
            </div>

            {/* Form */}
            <form className="mt-7 space-y-5">

                {/* Shop Name */}
                <div>
                    <label
                        htmlFor="shop-name"
                        className="mb-2 block text-sm font-medium text-slate-700"
                    >
                        Shop Name
                    </label>

                    <input
                        ref={shopNameRef}
                        id="shop-name"
                        name="shopName"
                        type="text"
                        minLength={4}
                        maxLength={64}
                        required
                        placeholder="e.g. My Barbershop"
                        autoComplete="organization"
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                    />

                    <p className="mt-2 text-xs text-slate-400">
                        Choose a name your customers will recognize.
                    </p>
                </div>

                {/* Action */}
                <button
                    ref={submittingRef}
                    type="button"
                    className="btn inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-3 font-medium"
                    onClick={handleCreateShop}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="h-5 w-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                        />
                    </svg>

                    Create Shop
                </button>
            </form>

            {/* Help */}
            <p className="mt-5 text-center text-xs text-slate-400">
                You can update your shop details later.
            </p>
        </div>
    </div>
);
    }

    const sections = [
        {
            label: "Workspace",
            items: [
                { id: "overview", icon: "⌂", label: "Overview" },
                { id: "calendar", icon: "▦", label: "Calendar" },
                { id: "bookings", icon: "◫", label: "Bookings", badge: pendingCount },
                { id: "customers", icon: "◎", label: "Customers", badge: customerCount },
                { id: "calendars", icon: "▣", label: "Resources" },
            ],
        },
        {
            label: "Client",
            items: [
                { id: "portal", icon: "🌐", label: "Client portal" },
                { id: "widget", icon: "🔌", label: "Website widget" },
            ],
        },
    ];

    const meta = META[tab] || META.overview;
    const shared = { shop, plans, resourceTypes, resources, bookings };

    return (
        <div className="booking-full">
            {((impersonate && onExit) || notice || error) && (
                <div className="max-w-[1400px] mx-auto w-full px-3 sm:px-6 pt-3 space-y-3">
                    {(impersonate && onExit) && (
                        <div className="hk-card flex flex-wrap justify-between items-center gap-2" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                            <span className="text-sm">
                                🕶️ <strong>Impersonating {shop.business}</strong>
                                <span className="text-[#92400e]"> — everything you do here applies to this shop.</span>
                            </span>
                            <button className="hk-btn hk-btn-dark hk-btn-sm" onClick={onExit}>
                                ← Back to admin
                            </button>
                        </div>
                    )}
                    {notice && (
                        <div className="alert alert-success alert-dismissible mb-0">
                            {notice}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingNotice())}></button>
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-danger alert-dismissible mb-0">
                            {error}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingError())}></button>
                        </div>
                    )}
                </div>
            )}

            <BookingShell
                brand={shop.business}
                sub={`${shop.plan === "pro" ? "Pro" : "Free"} plan · ${shop.status}`}
                sections={sections}
                active={tab}
                onNav={setTab}
                title={meta.title}
                subtitle={meta.subtitle}
                actions={impersonate ? null : <ShopSwitcher shops={shops} value={shop.id} onChange={pickShop} />}
                foot={
                    <div className="px-2 py-1 text-[11px] text-[#64748b]">
                        {myBookings.filter(isActiveBooking).length} active bookings · {shop.plan === "pro" ? "Pro" : "Free"} plan
                    </div>
                }
            >
                {tab === "overview" && (
                    <ShopOverview
                        {...shared}
                        onGoBookings={() => setTab("bookings")}
                        onGoCalendars={() => setTab("calendars")}
                    />
                )}
                {tab === "calendar" && (
                    <CalendarTab
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onBook={(payload) => dispatch(createBooking(payload))}
                    />
                )}
                {tab === "bookings" && (
                    <ShopBookings
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                    />
                )}
                {tab === "customers" && (
                    <CustomersTab
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                    />
                )}
                {tab === "calendars" && (
                    <CalendarsTab
                        {...shared}
                        onAddType={(payload) => dispatch(addShopType({ shopId: shop.id, payload }))}
                        onUpdateType={(id, patch) => dispatch(updateShopType({ id, patch }))}
                        onDeleteType={(id) => dispatch(deleteShopType(id))}
                        onAddResource={(typeId, name) => dispatch(addResource({ shopId: shop.id, typeId, name }))}
                        onRenameResource={(id, name) => dispatch(updateResource({ id, patch: { name } }))}
                        onDeleteResource={(id) => dispatch(deleteResource(id))}
                    />
                )}
                {tab === "portal" && (
                    <PortalTab
                        {...shared}
                        onBook={(payload) => dispatch(createBooking(payload))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onCancel={(id) => dispatch(updateBookingStatus({ id, status: "cancelled" }))}
                    />
                )}
                {tab === "widget" && (
                    <WidgetTab
                        {...shared}
                        onBook={(payload) => dispatch(createBooking(payload))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onCancel={(id) => dispatch(updateBookingStatus({ id, status: "cancelled" }))}
                    />
                )}
            </BookingShell>
        </div>
    );
}

export default Booking;
